import { selectCurrentHotelId } from 'accessors'
import { postAndSelectSubsidiary, savePrevPath } from 'actions'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import EmailIcon from 'components/ReusableComponents/Icons/Email'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import useCopyText from 'hooks/useCopyText'
import { set } from 'lodash'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import subsidiariesApi from 'services/api/subsidiaries'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'

import useLocalStorage from '../../hooks/useLocalStorageState'
import { HotelCard } from './HotelCard'
import styles from './Hotels.module.scss'
import { filterAndSort } from './sortHelpers'
import { TitleRow } from './TitleRow'

const HotelsByLocation = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [sortParam, setSortParam] = useLocalStorage('hotelsSortParam', 'updatedOn')

  const [selectedParentChain, setSelectedParentChain] = useState('all')
  const [selectedChain, setSelectedChain] = useState('all')
  const [selectedGroup, setSelectedGroup] = useState('all')
  const [selectedState, setSelectedState] = useState('all')
  const [selectedCity, setSelectedCity] = useState('all')

  useEffect(() => {
    setSelectedCity('all')
  }, [selectedState])

  const [hotels, setHotels] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const [allChains, setAllChains] = useState({})
  const [allGroups, setAllGroups] = useState({})
  const [updatedHotelId, setUpdatedHotelId] = useState(0)
  const [useCache, setUseCache] = useState(false)
  const copy = useCopyText()

  const [refetchIndex, setRefetchIndex] = useState(0)

  const dispatch = useDispatch()

  const currentId = useSelector(selectCurrentHotelId)

  function updateResults(id) {
    setUpdatedHotelId(id)
    setUseCache(true)
    setRefetchIndex(refetchIndex + 1)
  }

  useEffect(() => {
    subsidiariesApi
      .getHotelList({ type: 'allActive', refreshStatus: useCache })
      .then(res => {
        setAllChains(
          res.reduce((prev, curr) => {
            return {
              ...prev,
              [curr.chainCode]: curr.chainCode === 'i' ? 'independent' : curr.chainCode
            }
          }, {})
        )

        setAllGroups(
          res.reduce((prev, curr) => {
            return {
              ...prev,
              [curr.merchantName]:
                curr.merchantName === 'Independently Managed Hotels'
                  ? '- Independently Managed'
                  : curr.merchantName
            }
          }, {})
        )

        setHotels(res)
        setIsLoading(false)
        setUseCache(false)
        if (updatedHotelId === currentId) {
          dispatch(savePrevPath('/hotels-by-location'))
          dispatch(postAndSelectSubsidiary(currentId))
        }
      })
  }, [refetchIndex])

  const activeGroupHotels = filterAndSort(
    hotels,
    searchQuery,
    false,
    selectedChain,
    selectedParentChain,
    selectedGroup,
    sortParam,
    selectedState,
    selectedCity
  )

  const statesWithHotels = hotels
    .filter(h => !h.isDeleted)
    .reduce((prev, curr) => {
      if (curr.stateCode) {
        return {
          ...prev,
          [curr.stateCode]:
            { ...usStates, ...canadianProvinces }[curr.stateCode] || curr.stateCode
        }
      }
      return prev
    }, {})

  if (isLoading) {
    return <LoadingIndicator />
  }

  const hotelsWithSupportEmail = activeGroupHotels.filter(h => !!h.primaryUserMail)

  const supportEmails = hotelsWithSupportEmail.map(h => h.primaryUserMail).join(',')

  return (
    <div className={styles.wrapper}>
      <div className={styles.hotels}>
        <ReactTooltip />
        <CustomHeader title={`Hotels (${activeGroupHotels.length})`} isFixed width={1040}>
          <a href={`mailto:?bcc=${supportEmails}`} className={styles.newHotelLink}>
            <button
              className={styles.addHotelButton}
              onClick={() =>
                copy(supportEmails, `${hotelsWithSupportEmail.length} emails`, false)
              }
            >
              <EmailIcon fill="white" style={{ marginRight: 4, width: 15 }} />
              Email to all {hotelsWithSupportEmail.length}
            </button>
          </a>
        </CustomHeader>
        <ReactTooltip id="hotelsTooltip" />
        <div className={styles.header}>
          <Dropdown
            options={{
              all: ' Any state or province',
              ...statesWithHotels
            }}
            style={{ width: '180px' }}
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
          />
          <Dropdown
            disabled={selectedState === 'all'}
            style={{ width: '150px' }}
            options={activeGroupHotels.reduce(
              (prev, curr) => {
                if (curr.city === '' || curr.city === null) {
                  return prev
                }

                return {
                  ...prev,
                  [curr.city]: curr.city || 'Other'
                }
              },
              { all: 'Any city' }
            )}
            value={selectedCity}
            onChange={e => setSelectedCity(e.target.value)}
          />

          <Dropdown
            options={{
              all: ' All groups',
              ...allGroups
            }}
            style={{ width: 150 }}
            value={selectedGroup}
            onChange={e => setSelectedGroup(e.target.value)}
          />
          <Dropdown
            options={{
              all: ' All brands',
              ...parentChainNames
            }}
            value={selectedParentChain}
            onChange={e => setSelectedParentChain(e.target.value)}
          />
          <Dropdown
            options={{
              all: ' All chains',
              ...allChains
            }}
            value={selectedChain}
            onChange={e => setSelectedChain(e.target.value)}
          />
          <input
            type="search"
            className={styles.searchFilter}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Type anything to filter"
          />
        </div>
        <div className={styles.container}>
          <TitleRow handleClickTitle={setSortParam} sortParam={sortParam} />
          {activeGroupHotels.map(hotel => (
            <HotelCard {...hotel} updateResults={updateResults} key={hotel.id} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default HotelsByLocation
